import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  formatPercent,
  getAchievementToneClass,
  formatCurrency,
} from "../utils/formatters";
import "./MetricsSummary.css";

export default function MetricsSummary({
  loading,
  error,
  summary,
  unit, 
  setUnit 
}) {
  const unitOptions = useMemo(
    () => [
      { value: "absolute", label: "Absolute" },
      { value: "lakh", label: "Lakh" },
      { value: "crore", label: "Crore" },
    ],
    []
  );

  const selectedUnitLabel =
    unitOptions.find((o) => o.value === unit)?.label || "Lakh";

  const [unitMenuOpen, setUnitMenuOpen] = useState(false);
  const unitMenuRef = useRef(null);

  useEffect(() => {
    if (!unitMenuOpen) return;

    const onPointerDown = (e) => {
      if (!unitMenuRef.current) return;
      if (unitMenuRef.current.contains(e.target)) return;
      setUnitMenuOpen(false);
    };

    const onKeyDown = (e) => {
      if (e.key === "Escape") setUnitMenuOpen(false);
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [unitMenuOpen]);

  if (loading) {
    return (
      <section className="panel summary-panel">
        <div className="section-loader">
          <span className="loader-spinner" aria-hidden="true" />
          <span className="loader-text">
            Loading summary metrics<span className="loader-dots" aria-hidden="true" />
          </span>
        </div>
        <div className="metrics-header">
          <div className="metrics-left">
            <div className="skeleton-line w-70" />
          </div>
          <div className="metrics-right">
            <div className="skeleton-pill w-40" />
          </div>
        </div>
        <div className="summary-cards">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="metric-card skeleton-card">
              <div className="skeleton-line w-40" />
              <div className="skeleton-line w-70" />
            </div>
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="panel summary-panel">
        <div className="section-error">{error}</div>
      </section>
    );
  }

  if (!summary) return null;

  // ✅ USE PATH FROM SUMMARY (MAIN FIX)
  const pathText =
    Array.isArray(summary?.path) && summary.path.length > 0
      ? summary.path.join(" → ")
      : "";

  return (
    <section className="panel summary-panel">

      {/* ✅ HEADER (FIXED STRUCTURE) */}
      <div className="metrics-header">
        <div className="metrics-left">
          <p className="metrics-text">
            Showing metrics for: <strong>{pathText}</strong>
          </p>
        </div>

        <div className="metrics-right">
          <div className="unit-dropdown" ref={unitMenuRef}>
            <button
              type="button"
              className="unit-trigger"
              aria-haspopup="listbox"
              aria-expanded={unitMenuOpen}
              onClick={() => setUnitMenuOpen((v) => !v)}
            >
              <span className="unit-trigger-label">{selectedUnitLabel}</span>
              <span className="unit-trigger-caret" aria-hidden="true" />
            </button>

            {unitMenuOpen && (
              <div className="unit-menu" role="listbox" aria-label="Select unit">
                {unitOptions.map((opt) => {
                  const isSelected = opt.value === unit;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      className={`unit-option ${isSelected ? "is-selected" : ""}`}
                      onClick={() => {
                        setUnit(opt.value);
                        setUnitMenuOpen(false);
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ CARDS */}
      <div className="summary-cards">

        {/* SALES */}
        <div className="metric-card">
          <div className="metric-label">Sales</div>
          <div className="metric-value">
            {formatCurrency(
              summary.totalSales ?? summary.sales ?? 0,
              unit
            )}
          </div>
        </div>

        {/* TARGET */}
        <div className="metric-card">
          <div className="metric-label">Target</div>
          <div className="metric-value">
            {formatCurrency(
              summary.target ?? 0,
              unit
            )}
          </div>
        </div>

        {/* ACHIEVEMENT */}
        <div className="metric-card">
          <div className="metric-label">Achievement</div>
          <div
            className={`metric-value achievement-text ${getAchievementToneClass(
              summary.achievement ?? 0
            )}`}
          >
            {formatPercent(summary.achievement ?? 0)}
          </div>
        </div>

        {/* EMPLOYEES */}
        <div className="metric-card">
          <div className="metric-label">Employees</div>
          <div className="metric-value">
            {summary.totalEmployees ?? summary.employees ?? 0}
          </div>
        </div>

      </div>
    </section>
  );
}